import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, User, ArrowRight, Newspaper, FileText, Download } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Render text with support for markdown-style links [text](url) and bare URLs.
// Returns an array of React nodes; preserves the original line breaks via whitespace-pre-line.
const renderRichText = (text) => {
  if (!text) return null;
  const nodes = [];
  // Combined regex: markdown link OR bare URL (http(s)/www)
  const pattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+|www\.[^\s<]+)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[1] && match[2]) {
      // Markdown link: [text](url)
      nodes.push(
        <a
          key={`lnk-${key++}`}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-600 hover:text-emerald-700 underline break-words"
        >
          {match[1]}
        </a>
      );
    } else if (match[3]) {
      // Bare URL
      const raw = match[3];
      // Strip trailing punctuation that's almost certainly not part of the URL
      const trailingMatch = raw.match(/[).,;!?]+$/);
      const trailing = trailingMatch ? trailingMatch[0] : '';
      const url = trailing ? raw.slice(0, -trailing.length) : raw;
      const href = url.startsWith('www.') ? `https://${url}` : url;
      nodes.push(
        <a
          key={`url-${key++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-600 hover:text-emerald-700 underline break-words"
        >
          {url}
        </a>
      );
      if (trailing) nodes.push(trailing);
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

// Map an attachment URL/filename to a typed badge (label + color + accent classes).
const getFileBadge = (urlOrName) => {
  const s = (urlOrName || '').toLowerCase().split(/[?#]/)[0];
  const ext = s.slice(s.lastIndexOf('.') + 1);
  switch (ext) {
    case 'pdf':
      return { label: 'PDF', badge: 'bg-red-100 text-red-700 border-red-200', icon: 'bg-red-600' };
    case 'doc':
    case 'docx':
      return { label: 'DOC', badge: 'bg-blue-100 text-blue-700 border-blue-200', icon: 'bg-blue-600' };
    case 'xls':
    case 'xlsx':
      return { label: 'XLS', badge: 'bg-green-100 text-green-700 border-green-200', icon: 'bg-green-600' };
    default:
      return { label: 'FILE', badge: 'bg-gray-100 text-gray-700 border-gray-200', icon: 'bg-gray-600' };
  }
};

export const News = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news/`);
      setArticles(response.data);
    } catch (err) {
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'news': return 'bg-blue-100 text-blue-800';
      case 'update': return 'bg-emerald-100 text-emerald-800';
      case 'announcement': return 'bg-purple-100 text-purple-800';
      case 'event': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'news': return 'News';
      case 'update': return 'Update';
      case 'announcement': return 'Announcement';
      case 'event': return 'Event';
      default: return category;
    }
  };

  if (loading) {
    return (
      <section id="news" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="news" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-emerald-100 text-emerald-800 px-4 py-2 text-sm font-medium mb-4">
            News & Updates
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Latest from
            <span className="text-emerald-600"> Three Thirds Society</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Stay informed about our latest projects, events, and initiatives that are making a difference in communities across Greece.
          </p>
        </div>

        {/* Articles Grid */}
        {articles.length === 0 ? (
          <div className="text-center py-12">
            <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No news yet</h3>
            <p className="text-gray-500">Check back soon for updates from Three Thirds Society.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <Card 
                key={article.id} 
                className="bg-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden cursor-pointer"
                onClick={() => setSelectedArticle(article)}
              >
                {article.image && (
                  <div className="h-48 overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img 
                      src={article.image} 
                      alt={article.title}
                      className="max-w-full max-h-full object-contain transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={getCategoryColor(article.category)}>
                      {getCategoryLabel(article.category)}
                    </Badge>
                    <div className="flex items-center text-gray-500 text-sm">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(article.publishedAt)}
                    </div>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 line-clamp-2">
                    {article.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 line-clamp-3 mb-4">{article.summary}</p>
                  {article.author && (
                    <div className="flex items-center text-gray-500 text-sm">
                      <User className="w-4 h-4 mr-1" />
                      {article.author}
                    </div>
                  )}
                  {(article.document || (article.documents && article.documents.length > 0)) && (
                    <div className="flex items-center text-emerald-600 text-sm mt-2">
                      <FileText className="w-4 h-4 mr-1" />
                      <span>
                        {(() => {
                          const count = (article.documents?.length || 0) + (article.document && !(article.documents || []).some(d => d.url === article.document) ? 1 : 0);
                          return count > 1 ? `${count} attachments available` : 'Attachment available';
                        })()}
                      </span>
                    </div>
                  )}
                  <div className="mt-4 flex items-center text-emerald-600 font-medium group">
                    Read more 
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Article Modal */}
        {selectedArticle && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setSelectedArticle(null)}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {selectedArticle.image && (
                <div className="h-64 overflow-hidden bg-gray-100 flex items-center justify-center">
                  <img 
                    src={selectedArticle.image} 
                    alt={selectedArticle.title}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="flex items-center justify-between mb-4">
                  <Badge className={getCategoryColor(selectedArticle.category)}>
                    {getCategoryLabel(selectedArticle.category)}
                  </Badge>
                  <button
                    onClick={() => setSelectedArticle(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedArticle.title}</h2>
                <div className="flex items-center text-gray-500 text-sm mb-4 space-x-4">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {formatDate(selectedArticle.publishedAt)}
                  </div>
                  {selectedArticle.author && (
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      {selectedArticle.author}
                    </div>
                  )}
                </div>
                <div className="prose prose-emerald max-w-none">
                  <p className="text-gray-600 whitespace-pre-line break-words">
                    {renderRichText(selectedArticle.content)}
                  </p>
                </div>
                
                {/* Document Downloads */}
                {(() => {
                  const docs = [...(selectedArticle.documents || [])];
                  if (selectedArticle.document && !docs.some(d => d.url === selectedArticle.document)) {
                    docs.unshift({ url: selectedArticle.document, name: selectedArticle.documentName || 'Attached Document' });
                  }
                  if (docs.length === 0) return null;
                  return (
                    <div className="mt-6 space-y-3">
                      <h3 className="text-sm font-semibold text-gray-700">
                        {docs.length > 1 ? 'Attachments' : 'Attachment'}
                      </h3>
                      {docs.map((doc, idx) => {
                        const badge = getFileBadge(doc.url || doc.name);
                        return (
                          <div
                            key={`${doc.url}-${idx}`}
                            className="p-4 bg-white rounded-lg border border-gray-200 hover:border-emerald-300 hover:shadow-sm transition-all"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3 min-w-0">
                                <div
                                  className={`flex-shrink-0 w-12 h-12 rounded-md ${badge.icon} text-white flex items-center justify-center font-bold text-xs tracking-wide shadow-sm`}
                                  data-testid={`news-attachment-badge-${badge.label.toLowerCase()}`}
                                >
                                  {badge.label}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 truncate">
                                    {doc.name || 'Attached Document'}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${badge.badge} mr-2`}>
                                      {badge.label}
                                    </span>
                                    Click to download
                                  </p>
                                </div>
                              </div>
                              <a
                                href={doc.url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex-shrink-0 ml-3"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              <div className="p-4 border-t bg-gray-50">
                <button
                  onClick={() => setSelectedArticle(null)}
                  className="w-full px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
